import React, { useState } from "react";
import { motion } from "framer-motion";
import { LockKey, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * The "subscribe to unlock today's picks" gate that sits on the public landing
 * in place of the admin-only upcoming-picks list.
 */
export default function SubscribeCard({ upcomingCount = 0 }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/subscribe", { email });
      setDone(true);
      toast.success("You're on the list. Tomorrow's picks land in your inbox at 11am ET.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscribe failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden bg-gradient-to-br from-[#0d1a2b] via-[#0a0a0a] to-[#1a0d0d] border border-[#007AFF]/30 rounded-sm p-8 md:p-10"
      data-testid="subscribe-card"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{ backgroundImage: "radial-gradient(white 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#007AFF] mb-2">
            <LockKey size={14} weight="fill" /> Subscribers only
          </div>
          <h2 className="font-display text-4xl sm:text-5xl leading-[0.95] mb-4">
            TODAY&apos;S PICKS<br/>
            <span className="text-[#007AFF]">ARE LOCKED.</span>
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-lg">
            {upcomingCount > 0 ? (
              <>
                <span className="font-bold text-white">{upcomingCount} {upcomingCount === 1 ? "play is" : "plays are"}</span> queued for the upcoming slate.
                Drop your email and we&apos;ll send tomorrow&apos;s picks straight to your inbox at 11am ET — free for the rest of the season.
              </>
            ) : (
              "No live plays right now. Get on the list and we'll ping you the moment Charlie or Dakota lock the next one in."
            )}
          </p>
          <ul className="text-xs text-zinc-500 mt-5 space-y-1.5">
            <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-[#22C55E]" /> Full team + opponent disclosure</li>
            <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-[#22C55E]" /> Suggested stake size for the day</li>
            <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-[#22C55E]" /> Win-loss recap the next morning</li>
          </ul>
        </div>

        <div className="bg-black/40 border border-white/10 rounded-sm p-6">
          {done ? (
            <div className="text-center py-6" data-testid="subscribe-success">
              <CheckCircle size={48} weight="fill" className="text-[#22C55E] mx-auto mb-3" />
              <div className="font-display text-3xl mb-2">YOU&apos;RE IN.</div>
              <div className="text-xs text-zinc-400">Check your inbox at 11am ET tomorrow.</div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Your email</label>
                <div className="flex items-center gap-2 mt-2 border border-white/15 rounded-sm bg-[#0A0A0A] px-3 focus-within:border-[#007AFF]">
                  <EnvelopeSimple size={16} className="text-zinc-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@inbox.com"
                    data-testid="subscribe-email-input"
                    className="flex-1 bg-transparent text-sm font-mono-d py-2.5 outline-none"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="subscribe-submit-btn"
                className="w-full py-3 bg-[#007AFF] hover:bg-[#0064D6] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
              >
                {submitting ? "Submitting…" : "Unlock Today's Picks"}
              </button>
              <p className="text-[10px] text-zinc-600 text-center">No spam. Unsubscribe in one click.</p>
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
}
